/**
 * Common request recommendations shown in the right sidebar on add/edit forms.
 * Each item has a short label (1–2 words) for the sidebar and full text to populate form fields.
 * Section keys match community sections (lost-found, urgent-help, ask-for-help, etc.).
 * Lost & Found entries live in gedoba/lost-found/recommendations.ts.
 */

import {
  LOST_RECOMMENDATIONS,
  FOUND_RECOMMENDATIONS,
} from "../lost-found/recommendations";
import type {
  RequestRecommendation,
  RequestRecommendationSection,
} from "./requestRecommendationTypes";

export type {
  RequestRecommendation,
  RequestRecommendationSection,
} from "./requestRecommendationTypes";

/** Single combined section for legacy/cross-page use. */
export const LOST_FOUND_RECOMMENDATIONS_COMBINED: RequestRecommendationSection =
{
  title: "Lost & Found",
  items: [...LOST_RECOMMENDATIONS.items, ...FOUND_RECOMMENDATIONS.items],
};

/** Recommendations by section. Use the key that matches the current page (e.g. 'lost-found'). */
export const REQUEST_RECOMMENDATIONS: Record<
  string,
  RequestRecommendationSection
> = {
  "lost-found": LOST_FOUND_RECOMMENDATIONS_COMBINED,
  "immediate-help": {
    title: "Time Sensitive Help",
    items: [
      {
        label: "Locked out",
        text: "I'm locked out of my unit and need help right now",
      },
      { label: "Car boost", text: "My car won't start and I need a boost" },
      {
        label: "Lost keys",
        text: "I just lost my keys in the building, has anyone seen them",
      },
      {
        label: "Package taken",
        text: "A package was taken by mistake and I need it immediately",
      },
      {
        label: "Heavy lifting",
        text: "I need immediate help carrying something heavy",
      },
      {
        label: "Urgent issue",
        text: "Is anyone available right now to help with an urgent issue",
      },
    ],
  },
  "ask-for-help": {
    title: "Ask for Help",
    items: [
      {
        label: "Ride to store",
        text: "Can someone give me a ride to the grocery store",
      },
      {
        label: "Babysitter",
        text: "Does anyone babysit or know a trusted babysitter",
      },
      {
        label: "Borrow ladder/tools",
        text: "Can I borrow a ladder or basic tools",
      },
      {
        label: "Dog walking",
        text: "Is anyone available to walk my dog this week",
      },
      {
        label: "Light cleaning",
        text: "Can someone help with light cleaning or home tasks",
      },
      {
        label: "Building issue",
        text: "Does anyone know a good way to handle a common building issue",
      },
    ],
  },
  "share-with-neighbors": {
    title: "Share with Neighbors",
    items: [
      {
        label: "Extra groceries",
        text: "I have extra groceries and food to share",
      },
      {
        label: "Sofa or furniture",
        text: "Giving away a sofa or furniture in good condition",
      },
      {
        label: "Lend tools",
        text: "I can lend tools or household items if anyone needs them",
      },
      { label: "Home-cooked food", text: "Extra home-cooked food available" },
      {
        label: "Moving boxes",
        text: "I have extra moving boxes if someone wants them",
      },
      {
        label: "Plants or items",
        text: "Sharing plants or household items I no longer need",
      },
    ],
  },
  "other-requests": {
    title: "Other Requests",
    items: [
      {
        label: "Walking/fitness group",
        text: "Is anyone interested in forming a walking or fitness group",
      },
      {
        label: "Coordinate with neighbors",
        text: "Looking to coordinate something with a few neighbors",
      },
      {
        label: "Building question",
        text: "General question about living in the building",
      },
      {
        label: "Community ideas",
        text: "Suggestions or ideas for community improvements",
      },
      {
        label: "Group activity",
        text: "Organizing a small group activity or gathering",
      },
      {
        label: "Doesn't fit elsewhere",
        text: "A request that doesn't clearly fit in other sections",
      },
    ],
  },
};

/** Get recommendations for a section, or empty array if section not found. */
export function getRequestRecommendations(
  sectionKey: string,
): RequestRecommendationSection | null {
  return REQUEST_RECOMMENDATIONS[sectionKey] ?? null;
}
